import React from 'react';
import { ClassBookingDialog } from "@/components/classes/ClassBookingDialog";
import { EditClassDialog } from "@/components/schedule/EditClassDialog";
import { format } from 'date-fns';
import { useUserStore } from '@/store/userStore';
import type { CalendarScopedSubscription } from '@/lib/subscriptionCalendars';

interface WeeklyDialogsProps {
  isAdmin: boolean;
  isDetailsDialogOpen: boolean;
  setIsDetailsDialogOpen: (open: boolean) => void;
  selectedClassDetails: any;
  selectedClassDate: Date;
  isBookingDialogOpen: boolean;
  setIsBookingDialogOpen: (open: boolean) => void;
  selectedBookingClass: any;
  selectedBookingSession: any;
  onCapacityUpdate?: (classId: string, newCapacity: number) => Promise<void>;
  clientSubscriptionEndDate? : string;
  // `null` = valeur inconnue (lecture en échec), à ne pas confondre avec 0.
  clientRemainingSessions?: number | null;
  clientCoursesBooked?: number | null;
  /**
   * TOUS les abonnements actifs de l'adhérent, enrichis des calendriers que
   * leur type ouvre. `null` = lecture en échec. Quand cette liste est fournie,
   * elle prime sur les trois scalaires ci-dessus : le quota affiché devient
   * celui du planning du cours cliqué.
   */
  clientActiveSubscriptions?: CalendarScopedSubscription[] | null;
  /** Réservations à venir ventilées par `calendar_id`. `null` = non ventilé. */
  clientUpcomingByCalendar?: Record<string, number> | null;
  clientDataUnavailable?: boolean;
  onRetryClientData?: () => Promise<void>;
  handleDeleteClass?: (classId: string) => Promise<boolean>;
  classes: any[];
  refreshClasses?: () => Promise<void>;
  addRecentlyBookedSlot?: (classId: string, date: string, time: string) => void;
  isEditDialogOpen?: boolean;
  onEditDialogOpenChange?: (open: boolean) => void;
  editClassInfo?: any;
  editClassDate?: Date;
  onSaveEditedClass?: (values: { id?: string; name: string; instructor: string; categoryId: string; capacity: number; duration: string; time: string; courseId?: string }, date: Date) => Promise<boolean>;
  onOpenEditDialog?: (classInfo: any, date: Date) => void;
}

export const WeeklyDialogs: React.FC<WeeklyDialogsProps> = ({
  isAdmin,
  isDetailsDialogOpen,
  setIsDetailsDialogOpen,
  selectedClassDetails,
  selectedClassDate,
  isBookingDialogOpen,
  setIsBookingDialogOpen,
  selectedBookingClass,
  selectedBookingSession,
  onCapacityUpdate,
  clientSubscriptionEndDate,
  clientRemainingSessions,
  clientCoursesBooked,
  clientActiveSubscriptions,
  clientUpcomingByCalendar,
  clientDataUnavailable,
  onRetryClientData,
  handleDeleteClass,
  classes,
  refreshClasses,
  addRecentlyBookedSlot,
  isEditDialogOpen = false,
  onEditDialogOpenChange,
  editClassInfo,
  editClassDate = new Date(),
  onSaveEditedClass,
  onOpenEditDialog
}) => {

  const { role } = useUserStore();
  const isCoach = role === 'coach';
  const formattedDate = selectedBookingSession?.date ?
    format(new Date(selectedBookingSession.date), 'yyyy-MM-dd') :
    format(new Date(), 'yyyy-MM-dd');

  const handleEditClass = () => {
    setIsDetailsDialogOpen(false);
    if (selectedClassDetails && selectedClassDate && onOpenEditDialog) {
      onOpenEditDialog(selectedClassDetails, selectedClassDate);
    }
  };

  const editClassInfoWithCategoryId = editClassInfo ? {
    ...editClassInfo,
    categoryId: editClassInfo.categoryId ?? editClassInfo.category_id,
    courseId: editClassInfo.courseId ?? editClassInfo.course_id
  } : null;

  return (
    <>
      {/* Un seul dialogue de détails pour admin ET coach : les deux blocs
          montaient auparavant deux ClassBookingDialog avec le même `isOpen`,
          ce qui chargeait les participants deux fois et déclenchait chaque
          action en double pour un utilisateur ayant les deux rôles. */}
      {(isAdmin || isCoach) && selectedClassDetails && (
        <ClassBookingDialog
          isOpen={isDetailsDialogOpen}
          onOpenChange={setIsDetailsDialogOpen}
          classId={selectedClassDetails.id || ''}
          date={format(selectedClassDate, 'yyyy-MM-dd')}
          isScheduleView={true}
          onCapacityUpdate={onCapacityUpdate}
          handleDeleteClass={handleDeleteClass}
          classes={classes}
          onConfirm={refreshClasses}
          addRecentlyBookedSlot={addRecentlyBookedSlot}
          onEditClass={handleEditClass}
        />
      )}

      {!isAdmin && !isCoach && selectedBookingClass && (
        <ClassBookingDialog
          isOpen={isBookingDialogOpen}
          onOpenChange={setIsBookingDialogOpen}
          classId={selectedBookingClass.id || ''}
          date={formattedDate}
          isScheduleView={true}
          clientSubscriptionEndDate={clientSubscriptionEndDate}
          clientRemainingSessions={clientRemainingSessions}
          clientCoursesBooked={clientCoursesBooked}
          clientActiveSubscriptions={clientActiveSubscriptions}
          clientUpcomingByCalendar={clientUpcomingByCalendar}
          clientDataUnavailable={clientDataUnavailable}
          onRetryClientData={onRetryClientData}
          classes={classes}
          onConfirm={refreshClasses}
          addRecentlyBookedSlot={addRecentlyBookedSlot}
        />
      )}

      {isAdmin && isEditDialogOpen && editClassInfoWithCategoryId && onSaveEditedClass && onEditDialogOpenChange && (
        <EditClassDialog
          isOpen={isEditDialogOpen}
          onOpenChange={onEditDialogOpenChange}
          classInfo={editClassInfoWithCategoryId}
          date={editClassDate}
          onSave={onSaveEditedClass}
        />
      )}
    </>
  );
};
